import React, { FC, Ref, useEffect, useRef } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '../../inputs';
import { CoreIconButton } from '../../_core/core-button';
import { AttendeeRegistrationFieldTypeEnum, Colors } from '../../../constants';
import { getEmptyImage } from 'react-dnd-html5-backend';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrag, useDrop } from 'react-dnd';
import { AttendeeRegistrationFormDto } from '../../../models/dto';
import { clsx } from '../../../utils';
import { DeleteIcon, DragIcon } from '../../../icons';

export interface DragItem {
	id: string;
	index: number;
	fieldIndex: number;
}

export interface AttendeeRegistrationFieldOptionProps {
	id: string;
	index: number;
	fieldIndex: number;
	type: AttendeeRegistrationFieldTypeEnum;
	disabledRemoveButton?: boolean;
	sx?: SxProps<Theme>;
	onRemove?(): void;
	onMove?(index: number, newIndex: number): void;
}

export const AttendeeRegistrationFieldOption: FC<AttendeeRegistrationFieldOptionProps> = (props) => {
	const {
		id,
		index,
		fieldIndex,
		type,
		disabledRemoveButton = false,
		sx,
		onRemove,
		onMove,
	} = props;
	const { control } = useFormContext<AttendeeRegistrationFormDto>();
	const dropRef = useRef<HTMLDivElement | null>(null);

	const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
		accept: 'option',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item, monitor) {
			monitor.isOver({ shallow: true });
			const dragIndex = item.index;
			const hoverIndex = index;

			if (dragIndex === hoverIndex || !onMove || !dropRef.current) {
				return;
			}

			const hoverBoundingRect = dropRef.current.getBoundingClientRect();
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			const clientOffset = monitor.getClientOffset();
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			onMove(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag, preview] = useDrag({
		type: 'option',
		item: (): DragItem => ({ id, index, fieldIndex }),
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, [preview]);

	useEffect(() => {
		drop(dropRef);
	}, [drop]);

	return (
		<Box
			sx={clsx(
				{
					display: 'flex',
					alignItems: 'center',
					opacity: isDragging ? 0 : 1,

					'&:not(:last-child)': {
						mb: '8px',
					},
				},
				sx
			)}
			data-handler-id={handlerId}
			ref={dropRef}
		>
			<Box
				ref={drag as unknown as Ref<unknown>}
				sx={{
					display: 'flex',
					alignItems: 'center',
					mr: '4px',
					px: '6px',
					color: Colors.white,
					cursor: 'move',
				}}
			>
				<Box component={DragIcon} />
			</Box>
			<Controller
				control={control}
				name={`fields.${fieldIndex}.options.${index}.name`}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<TextField
						value={value}
						error={!!error}
						helperText={error?.message}
						onChange={(e) => {
							onChange(e.target.value);
						}}
						placeholder={
							type === AttendeeRegistrationFieldTypeEnum.RadioButtons ? `Choice ${index + 1}` : `Option ${index + 1}`
						}
						controlSx={{ flex: 1 }}
					/>
				)}
			/>
			<CoreIconButton
				onClick={onRemove}
				disabled={disabledRemoveButton}
				sx={{
					p: '5px',
					width: '32px',
					height: '32px',
					ml: '5px',

					'& > svg': {
						width: '22px',
						height: '22px',
					},
				}}
			>
				<Box
					component={DeleteIcon}
					sx={{
						color: disabledRemoveButton ? Colors.grayDark : Colors.white,
					}}
				/>
			</CoreIconButton>
		</Box>
	);
};
